import { PAY_TO } from '@spa/shared/constants/payTo';
import { MONTHS } from '@spa/shared/constants/months';
import { formatMoney } from '@spa/shared/utils';
import { calculateTotalBills, getBillNames } from '@spa/shared/utils/bills';
import { capitalizeFirstLetter } from '@spa/shared/utils/stringManipulation';
import { assembleFeeTitle } from '@spa/shared/utils/servicesExpenses';
import { OTHER_FEES_TYPE } from '@spa/shared/constants/otherFees';
import { CLEANING_PERIODICITY_TYPES } from '@spa/shared/constants/cleaningServices';
import { PRICE_LINE_TYPES } from '@spa/shared/constants/priceLine';
import { FEE_RECURRENCES } from '@spa/shared/constants/fees';

const { BILL, INCLUDED, DISCOUNT } = PRICE_LINE_TYPES;

const { ONE_TIME, MONTHLY, OPTIONAL } = FEE_RECURRENCES;

const PROMOCODE_TYPES = {
  SHARE_FACEBOOK: 'Facebook share discount',
  PARTNER_PROMO_CODE: 'Partner promo code',
  CUSTOMER_CARE: 'Client care discount'
};

const PAYMENT_PRIORITY_ASSERTIONS = [
  ({ type }) => type === INCLUDED,
  ({ type }) => !type || type === BILL,
  ({ type }) => type === DISCOUNT
];

const setPaymentPriority = payment => ({
  ...payment,
  priority: PAYMENT_PRIORITY_ASSERTIONS.findIndex(assertion => assertion(payment)) + 1
});

export const orderPaymentsByPriority = paymentsList => ({
  ...paymentsList,
  categories: paymentsList.categories.map(({ payments, ...category }) => ({
    ...category,
    payments: payments.map(setPaymentPriority).sort((paymentA, paymentB) => {
      const priorityA = paymentA.priority;
      const priorityB = paymentB.priority;
      if (priorityA === priorityB || (!priorityA && !priorityB)) {
        return 0;
      }

      return !priorityA || priorityA > priorityB ? 1 : -1;
    })
  }))
});

export const getPaymentsLists = (accommodationOffer, property, priceDetails, t) => {
  const [onBooking, onMoveIn, duringStay] = buildPaymentsLists(accommodationOffer, property, priceDetails, t).map(
    orderPaymentsByPriority
  );

  return { onBooking, onMoveIn, duringStay };
};

const buildPaymentsLists = (accommodationOffer, property, priceDetails, t) => [
  assembleOnBookingInformation(priceDetails, t),
  assembleOnMoveInInformation(accommodationOffer, property, priceDetails, t),
  assembleDuringStayInformation(priceDetails, t)
];

const assembleOnBookingInformation = (priceDetails, t) => {
  const {
    rents: {
      items: [firstRent]
    },
    fee: {
      values: { discount, capped: serviceFee },
      factors: { discount_type: discountType }
    },
    payments: { on_booking: onBooking }
  } = priceDetails;

  const firstRentalPayment = {
    key: 'first-payment',
    title: t('First rental payment'),
    value: formatMoney(firstRent.value.amount, firstRent.value.currency_code)
  };
  const serviceFeePayment = {
    key: 'service-fee',
    title: t('One-time service fee'),
    value: formatMoney(serviceFee.amount, serviceFee.currency_code)
  };
  const hasDiscount = discount.amount > 0;
  const payments = [
    firstRentalPayment,
    serviceFeePayment,
    ...(hasDiscount
      ? [
          {
            key: 'discount',
            title: t(`${PROMOCODE_TYPES[discountType?.replace(/-/g, '_').toUpperCase()] ?? 'Discount'}`),
            value: formatMoney(discount.amount, discount.currency_code),
            type: DISCOUNT
          }
        ]
      : [])
  ];
  const totalAmount = formatMoney(onBooking.amount, onBooking.currency_code);

  return { categories: [{ payments }], totalAmount };
};

const assembleOnMoveInInformation = (accommodationOffer, property, priceDetails, t) => {
  const {
    payments: { on_move_in: onMoveIn },
    admin_fee: { value: adminFee },
    deposit: { pay_to: securityDepositPayTo, value: securityDeposit }
  } = priceDetails;

  const landlordPolicies = accommodationOffer?.contract?.landlord_policies?.costs?.items;
  const hasSecurityDeposit = securityDepositPayTo === PAY_TO.ACCOMMODATION_PROVIDER && securityDeposit.amount > 0;
  const securityDepositLandlordPolicy = landlordPolicies?.find(
    policy => policy.type === OTHER_FEES_TYPE.SECURITY_DEPOSIT_IN_ADVANCE
  );
  const adminFeeLandordPolicy = landlordPolicies?.find(policy => policy.type === OTHER_FEES_TYPE.ADMIN_FEE_IN_ADVANCE);
  const securityDepositPayment =
    hasSecurityDeposit && !securityDepositLandlordPolicy
      ? {
          key: 'security-deposit',
          title: t('Security deposit'),
          recurrence: ONE_TIME,
          value: formatMoney(securityDeposit.amount, securityDeposit.currency_code)
        }
      : null;
  const adminFeePayment =
    adminFee.amount > 0 && !adminFeeLandordPolicy
      ? {
          key: 'admin-fee',
          title: t('Admin fee'),
          recurrence: ONE_TIME,
          value: formatMoney(adminFee.amount, adminFee.currency_code)
        }
      : null;

  const bills = accommodationOffer?.costs?.bills;
  const fixedMonthlyBillsPayments = getBillNames(accommodationOffer, property)
    ?.filter(billName => bills?.[billName]?.included)
    ?.map(billName => {
      const bill = bills[billName];
      const hasMaxAmount = bill.maximum?.max?.amount;
      const isIncluded = bill.included;

      return {
        key: billName,
        title: t(capitalizeFirstLetter(billName)),
        recurrence: MONTHLY,
        value:
          isIncluded && hasMaxAmount
            ? `${t('Up to')} ${formatMoney(bill.maximum.max.amount, bill.maximum.max.currency_code)}`
            : null,
        type: isIncluded && !hasMaxAmount ? INCLUDED : BILL
      };
    });
  const billsTotal = bills?.maximum?.max?.amount || calculateTotalBills(bills) || 0;

  const hasBillsPayment =
    billsTotal > 0 &&
    (!fixedMonthlyBillsPayments?.length || fixedMonthlyBillsPayments.every(({ type }) => type === INCLUDED));
  const billsPayment = hasBillsPayment
    ? {
        key: 'bills',
        title: t('Bills'),
        recurrence: MONTHLY,
        value: `${t('Up to')} ${formatMoney(billsTotal, onMoveIn.currency_code)}`
      }
    : null;

  const lastRent = priceDetails.rents.items[priceDetails.rents.items.length - 1].value;

  const landlordPoliciesPayments =
    landlordPolicies?.map(fee => ({
      key: fee.type,
      title: t(assembleFeeTitle(fee.type)),
      recurrence: fee.recurrence,
      value:
        fee.type === OTHER_FEES_TYPE.LAST_MONTH_RENT_IN_ADVANCE
          ? formatMoney(lastRent.amount, lastRent.currency_code)
          : formatMoney(fee.amount.amount, fee.amount.currency_code)
    })) || [];

  const cleaning = accommodationOffer?.costs?.services?.cleaning;
  const cleaningFee = landlordPolicies?.find(policy => policy.type === OTHER_FEES_TYPE.CLEANING_FEE);
  const isCleaningFeeIncluded = (cleaning?.periodicity || cleaning?.type) && !cleaningFee;
  const isFrequencySpecified =
    cleaning?.periodicity &&
    ![CLEANING_PERIODICITY_TYPES.NONE, CLEANING_PERIODICITY_TYPES.UNKNOWN].includes(cleaning?.periodicity);

  const payments =
    [
      ...fixedMonthlyBillsPayments,
      billsPayment,
      securityDepositPayment,
      adminFeePayment,
      ...landlordPoliciesPayments,
      ...(isCleaningFeeIncluded
        ? [
            {
              key: OTHER_FEES_TYPE.CLEANING_FEE,
              title: `${
                isFrequencySpecified ? capitalizeFirstLetter(t(`${cleaning.periodicity} cleaning`)) : t('Cleaning')
              }`,
              recurrence: OPTIONAL,
              type: INCLUDED
            }
          ]
        : [])
    ].filter(Boolean) || [];

  const [
    oneTimePayments,
    dailyPayments,
    fortnightlyPayments,
    monthlyPayments,
    yearlyPayments,
    optionalPayments
  ] = Object.values(FEE_RECURRENCES).map(recurrence => payments.filter(payment => payment.recurrence === recurrence));

  const optionalPaymentsFiltered = optionalPayments.filter(payment => !payment.value);
  const landlordPoliciesTotal =
    landlordPolicies?.reduce((acc, current) => {
      if (current.recurrence === FEE_RECURRENCES.OPTIONAL) {
        return acc;
      }

      return (
        acc + (current.type === OTHER_FEES_TYPE.LAST_MONTH_RENT_IN_ADVANCE ? lastRent.amount : current.amount.amount)
      );
    }, 0) || 0;
  const rest =
    (securityDepositLandlordPolicy ? 0 : securityDeposit.amount) + (adminFeeLandordPolicy ? 0 : adminFee.amount);
  const totalAmount = formatMoney(rest + landlordPoliciesTotal, onMoveIn.currency_code);

  return {
    categories: [
      ...(oneTimePayments.length > 0 ? [{ name: t('One-time payments'), payments: oneTimePayments }] : []),
      ...(monthlyPayments.length > 0 ? [{ name: t('Monthly payments'), payments: monthlyPayments }] : []),
      ...(dailyPayments.length > 0 ? [{ name: t('Daily payments'), payments: dailyPayments }] : []),
      ...(fortnightlyPayments.length > 0 ? [{ name: t('Fortnightly payments'), payments: fortnightlyPayments }] : []),
      ...(yearlyPayments.length > 0 ? [{ name: t('Yearly payments'), payments: yearlyPayments }] : []),
      ...(optionalPaymentsFiltered.length > 0
        ? [{ name: t('Optional services'), payments: optionalPaymentsFiltered }]
        : [])
    ],
    totalAmount
  };
};

const assembleDuringStayInformation = (priceDetails, t) => {
  const {
    rents: {
      items: [, ...rents]
    },
    payments: { during_stay: duringStay }
  } = priceDetails;

  const payments =
    rents?.map(({ month, year, value }) => ({
      key: `${month}-${year}-rent`,
      title: `${t(`${MONTHS[month]}`)} ${year}`,
      value: formatMoney(value.amount, value.currency_code)
    })) || [];
  const totalAmount = formatMoney(duringStay.amount, duringStay.currency_code);

  return { categories: [...(payments.length > 0 ? [{ payments }] : [])], totalAmount };
};
